import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead, { SITE_URL } from '@/components/feature/SEOHead';

const contactJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact White Oak Insurance Group',
    description:
      'Contact White Oak Insurance Group in Hattiesburg, MS. Reach out to our independent insurance agents for personal, business, or industry insurance quotes and policy questions.',
    url: `${SITE_URL}/contact`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'White Oak Insurance Group',
      url: SITE_URL,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'InsuranceAgency',
    name: 'White Oak Insurance Group',
    url: `${SITE_URL}/contact`,
    telephone: '+1-601-325-9000',
    email: 'cameron@whiteoakig.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '406 Hemphill St',
      addressLocality: 'Hattiesburg',
      addressRegion: 'MS',
      postalCode: '39401',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 31.3271,
      longitude: -89.2903,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '17:00',
      },
    ],
    areaServed: {
      '@type': 'State',
      name: 'Mississippi',
    },
  },
];

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    Array.from(new FormData(form)).forEach(([key, value]) => {
      data.append(key, value as string);
    });
    try {
      await fetch('https://readdy.ai/api/form/d76johjq7u9ee9hcegs0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Contact Us | White Oak Insurance Group – Hattiesburg, MS"
        description="Get in touch with White Oak Insurance Group in Hattiesburg, MS. Call 601-325-9000 or send a message for personal, business, or industry insurance quotes and policy support."
        keywords="contact White Oak Insurance Group, insurance quote Hattiesburg MS, insurance agency phone number Mississippi, 601-325-9000"
        canonical="/contact"
        jsonLd={contactJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 bg-[#0b2240]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b2240] to-[#3072b7]/30" />
        <div className="relative z-10 max-w-screen-xl mx-auto px-6 text-white text-center">
          <h1 className="text-5xl font-bold mb-4">Contact Us</h1>
          <div className="w-16 h-1 bg-[#9cbde5] mx-auto mb-5" />
          <p className="text-white/80 text-lg max-w-xl mx-auto">
            We'd love to hear from you. Reach out and let us help you find the right coverage.
          </p>
        </div>
      </section>

      <main className="py-20">
        <div className="max-w-screen-xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* Contact Info Sidebar */}
          <div className="flex flex-col gap-6">
            {/* Address Card */}
            <div className="bg-white border-t-4 border-[#3072b7] shadow-sm rounded-b-xl p-8">
              <h3 className="text-lg font-bold text-[#0b2240] mb-4">Hattiesburg, MS Office</h3>
              <div className="w-10 h-0.5 bg-[#3072b7] mb-5" />
              <div className="space-y-4 text-sm text-gray-600">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-map-pin-2-line text-[#3072b7] text-base" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0b2240]">Address</p>
                    <p>406 Hemphill St<br />Hattiesburg, MS 39401</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-phone-line text-[#3072b7] text-base" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0b2240]">Phone</p>
                    <a href="tel:6013259000" className="hover:text-[#3072b7]">601-325-9000</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-mail-line text-[#3072b7] text-base" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0b2240]">Email</p>
                    <a href="mailto:cameron@whiteoakig.com" className="hover:text-[#3072b7] break-all block">
                      cameron@whiteoakig.com
                    </a>
                    <a href="mailto:holley@whiteoakig.com" className="hover:text-[#3072b7] break-all block">
                      holley@whiteoakig.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-time-line text-[#3072b7] text-base" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0b2240]">Office Hours</p>
                    <p>Monday – Friday: 9:00 AM – 5:00 PM</p>
                    <p>Saturday – Sunday: Closed</p>
                  </div>
                </div>
              </div>

              {/* Social */}
              <div className="flex gap-3 mt-6">
                <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full border border-[#3072b7] text-[#3072b7] hover:bg-[#3072b7] hover:text-white transition-colors">
                  <i className="ri-facebook-fill text-sm" />
                </a>
                <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full border border-[#3072b7] text-[#3072b7] hover:bg-[#3072b7] hover:text-white transition-colors">
                  <i className="ri-linkedin-fill text-sm" />
                </a>
              </div>
            </div>

            {/* Map Embed */}
            <div className="rounded-xl overflow-hidden h-64 shadow-sm">
              <iframe
                title="Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3374.5!2d-89.2903!3d31.3271!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x889c0e1c6e6c6e6b%3A0x1!2s406+Hemphill+St%2C+Hattiesburg%2C+MS+39401!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Licensed States */}
            <div className="bg-[#0b2240] rounded-xl p-6 text-white text-center">
              <i className="ri-award-line text-3xl text-[#9cbde5] mb-3 block" />
              <h4 className="font-bold text-base mb-2">Licensed in Mississippi</h4>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2 bg-white border-t-4 border-[#3072b7] shadow-sm rounded-b-xl px-10 py-12">
            <h2 className="text-2xl font-bold text-[#0b2240] mb-2">Send Us a Message</h2>
            <p className="text-gray-500 text-sm mb-8">
              Fill out the form below and one of our advisors will get back to you shortly.
            </p>

            {submitted ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
                <div className="w-16 h-16 flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-5xl text-[#3072b7]" />
                </div>
                <h4 className="text-xl font-bold text-[#0b2240]">Thank You!</h4>
                <p className="text-gray-500 text-sm max-w-xs">
                  Your message has been received. We'll be in touch with you shortly.
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-[#0b2240] uppercase tracking-wide">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="first_name"
                      required
                      className="border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#3072b7] transition-colors"
                      placeholder="First name"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-[#0b2240] uppercase tracking-wide">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="last_name"
                      required
                      className="border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#3072b7] transition-colors"
                      placeholder="Last name"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-[#0b2240] uppercase tracking-wide">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      className="border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#3072b7] transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-[#0b2240] uppercase tracking-wide">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="phone"
                      type="tel"
                      required
                      className="border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#3072b7] transition-colors"
                      placeholder="(000) 000-0000"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[#0b2240] uppercase tracking-wide">
                    Type of Insurance
                  </label>
                  <select
                    name="insurance_type"
                    className="border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#3072b7] transition-colors bg-white"
                  >
                    <option value="">Select an option</option>
                    <option value="Personal Insurance">Personal Insurance</option>
                    <option value="Business Insurance">Business Insurance</option>
                    <option value="Industry Insurance">Industry Insurance</option>
                    <option value="Life Insurance">Life Insurance</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[#0b2240] uppercase tracking-wide">
                    How can we help? <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#3072b7] transition-colors resize-none"
                    placeholder="Please do not include sensitive, private information in this area."
                  />
                  <p className="text-xs text-gray-400">Max 500 characters</p>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto bg-[#3072b7] hover:bg-[#0b2240] text-white font-bold text-sm px-12 py-3.5 rounded-md transition-colors disabled:opacity-60 whitespace-nowrap"
                >
                  {submitting ? 'Sending...' : 'Submit'}
                </button>
              </form>
            )}

            <div className="mt-8 pt-6 border-t border-gray-100 text-center space-y-2">
              <p className="text-sm text-gray-500">
                Prefer to call? Reach us at{' '}
                <a href="tel:6013259000" className="text-[#3072b7] font-bold hover:underline">
                  601-325-9000
                </a>
              </p>
              <p className="text-sm text-gray-500">
                Or email us at{' '}
                <a href="mailto:cameron@whiteoakig.com" className="text-[#3072b7] font-bold hover:underline">
                  cameron@whiteoakig.com
                </a>
                {' '}or{' '}
                <a href="mailto:holley@whiteoakig.com" className="text-[#3072b7] font-bold hover:underline">
                  holley@whiteoakig.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
